import { Assets } from './assets.ts';
import { Person } from './person.ts';
import { StaticMarketData } from './market.ts';
import { Flow } from './flow.ts';

// Immutable instances
class RetirementState {
  static deserialize(s: string): RetirementState {
    const j = JSON.parse(s) as Record<string, number | string | string[]>;

    return new RetirementState(
      j.year as number,
      Person.deserialize(j.person as string),
      Assets.deserialize(j.assets as string),
      StaticMarketData.deserialize(j.market as string),
      (j.causal_flows as string[]).map(fs => Flow.deserialize(fs)),
    );
  }

  _year_start_date: Date;
  _cum_assets: Assets;
  _person: Person;
  _market: StaticMarketData;
  _causal_flows: Flow[];

  constructor(
    year: number,
    person: Person,
    assets: Assets,
    market: StaticMarketData,
    causal_flows: Flow[],
  ) {
    this._year_start_date = new Date(year, 4, 6);
    this._person = person;
    this._cum_assets = assets;
    this._market = market;
    this._causal_flows = causal_flows;
  }

  setAssets(newAssets: Assets): RetirementState {
    return new RetirementState(
      this.year,
      this._person,
      newAssets,
      this._market,
      this._causal_flows,
    );
  }

  serialize(): string {
    return JSON.stringify({
      year: this.year,
      person: this.person.serialize(),
      assets: this.assets.serialize(),
      market: this.market.serialize(),
      causal_flows: this.causal_flows.map(f => f.serialize()),
    });
  }

  toString(): string {
    return `${this.year} ${this.age.toFixed(1)} ${this._cum_assets.toString()}`;
  }

  get year_start_date(): Date {
    return this._year_start_date;
  }

  get year(): number {
    return this.year_start_date.getFullYear();
  }

  get assets(): Assets {
    return this._cum_assets;
  }

  get person(): Person {
    return this._person;
  }

  get market(): StaticMarketData {
    return this._market;
  }

  get age(): number {
    return (
      this.year_start_date.getTime() - this._person.birth_date.getTime()
    ) / (1000 * 60 * 60 * 24 * 365.2425);
  }

  get is_retired(): boolean {
    return this.year_start_date >= this.person.retirement_date;
    // return this.age() >= this._person.retirement_age()
  }

  get is_receiving_state_pension(): boolean {
    return this.age >= this._market.state_pension_age;
  }

  get actual_state_pension_pa(): number {
    return this.is_receiving_state_pension
      ? this._market.state_pension_pa
      : 0;
  }

  get is_receiving_final_salary_pension(): boolean {
    return this.age >= this._person.final_salary_pension_start_age
    && this._person.final_salary_pension_pa !== 0;
  }

  get actual_final_salary_pension_pa(): number {
    return this.is_receiving_final_salary_pension
      ? this._person.final_salary_pension_pa
      : 0;
  }

  get can_receive_private_pension(): boolean {
    return this.age >= this._person.private_pension_age;
  }

  /**
   * Returns the flows (if any) that led to this state.
   *
   * @remarks
   * Allows investigation/debugging/tracing.
   *
   */
  get causal_flows(): Flow[] {
    return this._causal_flows;
  }
}

export { RetirementState };
